import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useRef,
  type ReactNode,
} from 'react';
import toast from 'react-hot-toast';
import { dashboardApi } from '@/services/dashboard-api';
import type {
  Dashboard,
  DashboardBlock,
  DashboardControl,
  DashboardWithBlocks,
  BlockStatus,
  Position,
  RenderHandle,
  ControlValues,
} from '@/types/dashboard';
import type { ChatMessage, V2StreamEvent, MessageSegment } from '@/types/chat';

// ============================================================================
// STATE
// ============================================================================

export interface SystemNotice {
  id: string;
  text: string;
  timestamp: string;
  icon: 'check' | 'x';
}

interface DashboardBuildState {
  dashboard: Dashboard | null;
  blocks: Map<string, DashboardBlock>;
  blockData: Map<string, Record<string, unknown>[]>;
  controls: Map<string, DashboardControl>;
  activeControlValues: Record<string, unknown>;
  chatMessages: ChatMessage[];
  systemNotices: SystemNotice[];
  threadId: string | null;
  isLLMResponding: boolean;
  liveSegments: MessageSegment[];
  currentTextSegmentIndex: number | null;
  isRefreshing: boolean;
  isPreviewMode: boolean;
  isLoading: boolean;
  error: string | null;
}

const initialState: DashboardBuildState = {
  dashboard: null,
  blocks: new Map(),
  blockData: new Map(),
  controls: new Map(),
  activeControlValues: {},
  chatMessages: [],
  systemNotices: [],
  threadId: null,
  isLLMResponding: false,
  liveSegments: [],
  currentTextSegmentIndex: null,
  isRefreshing: false,
  isPreviewMode: false,
  isLoading: true,
  error: null,
};

// ============================================================================
// ACTIONS
// ============================================================================

type DashboardBuildAction =
  | {
      type: 'INIT_DASHBOARD';
      dashboard: DashboardWithBlocks;
      messages: ChatMessage[];
      threadId: string | null;
    }
  | { type: 'INIT_BLOCK_DATA'; blockData: Map<string, Record<string, unknown>[]> }
  | { type: 'BLOCKS_CREATED'; blocks: DashboardBlock[]; blockData: Map<string, Record<string, unknown>[]> }
  | { type: 'BLOCK_UPDATED'; block: DashboardBlock; data?: Record<string, unknown>[] }
  | { type: 'BLOCK_REMOVED'; blockId: string }
  | { type: 'CONTROL_CREATED'; control: DashboardControl }
  | { type: 'CONTROL_UPDATED'; control: DashboardControl }
  | { type: 'CONTROL_REMOVED'; controlId: string }
  | { type: 'SETTINGS_UPDATED'; settings: Partial<Dashboard> }
  | { type: 'DATA_REFRESHED'; blockData: Map<string, Record<string, unknown>[]> }
  | { type: 'CHAT_MESSAGES_LOADED'; messages: ChatMessage[] }
  | { type: 'CHAT_MESSAGE_ADDED'; messages: ChatMessage[] }
  | { type: 'SET_THREAD_ID'; threadId: string }
  | { type: 'LLM_RESPONDING'; responding: boolean }
  | { type: 'STREAMING_TEXT_SEGMENT_START' }
  | { type: 'STREAMING_TEXT_DELTA'; delta: string }
  | { type: 'STREAMING_TOOL_START'; toolName: string; toolCallId: string }
  | { type: 'STREAMING_TOOL_RESULT'; toolCallId: string; success: boolean }
  | { type: 'STREAMING_RESET' }
  | { type: 'BLOCK_STATUS_CHANGED'; blockId: string; status: BlockStatus }
  | { type: 'BLOCK_POSITIONS_UPDATED'; positions: { blockId: string; position: Position }[] }
  | { type: 'ADD_SYSTEM_NOTICE'; notice: SystemNotice }
  | { type: 'CONTROL_VALUE_CHANGED'; parameterKey: string; value: unknown }
  | { type: 'SET_REFRESHING'; refreshing: boolean }
  | { type: 'SET_PREVIEW_MODE'; preview: boolean }
  | { type: 'SET_ERROR'; error: string }
  | { type: 'SET_LOADING'; loading: boolean };

// ============================================================================
// REDUCER
// ============================================================================

function dashboardBuildReducer(
  state: DashboardBuildState,
  action: DashboardBuildAction
): DashboardBuildState {
  switch (action.type) {
    case 'INIT_DASHBOARD': {
      const blocks = new Map<string, DashboardBlock>();
      for (const block of action.dashboard.blocks) {
        blocks.set(block.id, block);
      }
      const controls = new Map<string, DashboardControl>();
      for (const ctrl of action.dashboard.controls) {
        controls.set(ctrl.id, ctrl);
      }
      return {
        ...state,
        dashboard: action.dashboard,
        blocks,
        controls,
        chatMessages: action.messages,
        threadId: action.threadId,
        isLoading: false,
        error: null,
      };
    }

    case 'INIT_BLOCK_DATA': {
      return {
        ...state,
        blockData: new Map([...state.blockData, ...action.blockData]),
      };
    }

    case 'BLOCKS_CREATED': {
      const newBlocks = new Map(state.blocks);
      const newData = new Map(state.blockData);
      for (const block of action.blocks) {
        newBlocks.set(block.id, block);
      }
      for (const [id, data] of action.blockData) {
        newData.set(id, data);
      }
      return { ...state, blocks: newBlocks, blockData: newData };
    }

    case 'BLOCK_UPDATED': {
      const newBlocks = new Map(state.blocks);
      newBlocks.set(action.block.id, action.block);
      const newData = new Map(state.blockData);
      if (action.data) {
        newData.set(action.block.id, action.data);
      }
      return { ...state, blocks: newBlocks, blockData: newData };
    }

    case 'BLOCK_REMOVED': {
      const newBlocks = new Map(state.blocks);
      newBlocks.delete(action.blockId);
      const newData = new Map(state.blockData);
      newData.delete(action.blockId);
      return { ...state, blocks: newBlocks, blockData: newData };
    }

    case 'CONTROL_CREATED': {
      const newControls = new Map(state.controls);
      newControls.set(action.control.id, action.control);
      return { ...state, controls: newControls };
    }

    case 'CONTROL_UPDATED': {
      const newControls = new Map(state.controls);
      newControls.set(action.control.id, action.control);
      return { ...state, controls: newControls };
    }

    case 'CONTROL_REMOVED': {
      const newControls = new Map(state.controls);
      newControls.delete(action.controlId);
      return { ...state, controls: newControls };
    }

    case 'SETTINGS_UPDATED': {
      if (!state.dashboard) return state;
      return {
        ...state,
        dashboard: { ...state.dashboard, ...action.settings },
      };
    }

    case 'DATA_REFRESHED': {
      return {
        ...state,
        blockData: new Map([...state.blockData, ...action.blockData]),
      };
    }

    case 'CHAT_MESSAGES_LOADED':
      return { ...state, chatMessages: action.messages };

    case 'CHAT_MESSAGE_ADDED': {
      const existingIds = new Set(state.chatMessages.map((m) => m.id));
      const newMsgs = action.messages.filter((m) => !existingIds.has(m.id));
      return {
        ...state,
        chatMessages: [...state.chatMessages, ...newMsgs],
      };
    }

    case 'SET_THREAD_ID':
      return { ...state, threadId: action.threadId };

    case 'LLM_RESPONDING':
      return { ...state, isLLMResponding: action.responding };

    case 'STREAMING_TEXT_SEGMENT_START': {
      const next = [...state.liveSegments, { type: 'text' as const, text: '' }];
      return { ...state, liveSegments: next, currentTextSegmentIndex: next.length - 1 };
    }

    case 'STREAMING_TEXT_DELTA': {
      const idx = state.currentTextSegmentIndex;
      if (idx === null) {
        const next = [...state.liveSegments, { type: 'text' as const, text: action.delta }];
        return { ...state, liveSegments: next, currentTextSegmentIndex: next.length - 1 };
      }
      const segs = [...state.liveSegments];
      const seg = segs[idx];
      if (seg?.type === 'text') {
        segs[idx] = { ...seg, text: seg.text + action.delta };
      }
      return { ...state, liveSegments: segs };
    }

    case 'STREAMING_TOOL_START':
      return {
        ...state,
        currentTextSegmentIndex: null,
        liveSegments: [
          ...state.liveSegments,
          { type: 'tool' as const, toolName: action.toolName, toolCallId: action.toolCallId, status: 'running' as const, latencyMs: 0 },
        ],
      };

    case 'STREAMING_TOOL_RESULT':
      return {
        ...state,
        liveSegments: state.liveSegments.map(s =>
          s.type === 'tool' && s.toolCallId === action.toolCallId
            ? { ...s, status: (action.success ? 'completed' : 'failed') as 'completed' | 'failed' }
            : s
        ),
      };

    case 'STREAMING_RESET':
      return { ...state, liveSegments: [], currentTextSegmentIndex: null };

    case 'BLOCK_STATUS_CHANGED': {
      const existing = state.blocks.get(action.blockId);
      if (!existing) return state;
      const newBlocks = new Map(state.blocks);
      newBlocks.set(action.blockId, { ...existing, status: action.status });
      return { ...state, blocks: newBlocks };
    }

    case 'BLOCK_POSITIONS_UPDATED': {
      const newBlocks = new Map(state.blocks);
      for (const { blockId, position } of action.positions) {
        const existing = newBlocks.get(blockId);
        if (existing) {
          newBlocks.set(blockId, { ...existing, position });
        }
      }
      return { ...state, blocks: newBlocks };
    }

    case 'ADD_SYSTEM_NOTICE':
      return {
        ...state,
        systemNotices: [...state.systemNotices, action.notice],
      };

    case 'CONTROL_VALUE_CHANGED':
      return {
        ...state,
        activeControlValues: {
          ...state.activeControlValues,
          [action.parameterKey]: action.value,
        },
      };

    case 'SET_REFRESHING':
      return { ...state, isRefreshing: action.refreshing };

    case 'SET_PREVIEW_MODE':
      return { ...state, isPreviewMode: action.preview };

    case 'SET_ERROR':
      return { ...state, error: action.error, isLoading: false };

    case 'SET_LOADING':
      return { ...state, isLoading: action.loading };

    default:
      return state;
  }
}

// ============================================================================
// CONTEXT
// ============================================================================

interface DashboardBuildContextValue {
  state: DashboardBuildState;
  renderHandles: React.MutableRefObject<Map<string, RenderHandle>>;
  sendMessage: (content: string) => Promise<void>;
  refreshData: () => Promise<void>;
  initDashboard: (dashboardId: string) => Promise<void>;
  updateBlockStatus: (blockId: string, status: BlockStatus) => Promise<void>;
  sendFeedback: (blockId: string, blockTitle: string, feedback: string) => Promise<void>;
  changeControlValue: (parameterKey: string, value: unknown) => Promise<void>;
  togglePreviewMode: () => void;
  saveDashboard: () => Promise<void>;
  updateBlockPositions: (positions: { blockId: string; position: Position }[]) => void;
  abortStream: () => void;
}

const DashboardBuildContext = createContext<DashboardBuildContextValue | null>(
  null
);

// ============================================================================
// PROVIDER
// ============================================================================

interface DashboardBuildProviderProps {
  children: ReactNode;
}

export const DashboardBuildProvider: React.FC<DashboardBuildProviderProps> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(dashboardBuildReducer, initialState);
  const renderHandles = useRef<Map<string, RenderHandle>>(new Map());
  const dashboardIdRef = useRef<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const threadIdRef = useRef<string | null>(null);

  const initDashboard = useCallback(async (dashboardId: string) => {
    dashboardIdRef.current = dashboardId;
    dispatch({ type: 'SET_LOADING', loading: true });

    try {
      const [dashResult, convResult] = await Promise.all([
        dashboardApi.getById(dashboardId),
        dashboardApi.getConversation(dashboardId),
      ]);

      const dashboard = dashResult.data;
      const messages = (convResult.messages ?? []) as ChatMessage[];
      const threadId = convResult.thread_id;

      threadIdRef.current = threadId;

      dispatch({
        type: 'INIT_DASHBOARD',
        dashboard,
        messages,
        threadId,
      });

      if (dashboard.blocks.length > 0) {
        try {
          const dataResult = await dashboardApi.fetchAllData(dashboardId);
          const blockData = new Map<string, Record<string, unknown>[]>();
          if (dataResult.data && Array.isArray(dataResult.data)) {
            for (const item of dataResult.data) {
              if (item.success && item.data) {
                blockData.set(item.block_id, item.data);
              }
            }
          }
          dispatch({ type: 'INIT_BLOCK_DATA', blockData });
        } catch {
          // Non-fatal: blocks render without data, user can refresh
        }
      }
    } catch (err) {
      dispatch({
        type: 'SET_ERROR',
        error: err instanceof Error ? err.message : 'Failed to load dashboard',
      });
    }
  }, []);

  /**
   * After the agent stream completes, reload the dashboard to pick up
   * any blocks/controls/settings the agent created or modified.
   */
  const reloadDashboardState = useCallback(async () => {
    const dashboardId = dashboardIdRef.current;
    if (!dashboardId) return;

    try {
      const dashResult = await dashboardApi.getById(dashboardId);
      const dashboard = dashResult.data;

      const newBlocks = new Map<string, DashboardBlock>();
      for (const block of dashboard.blocks) {
        newBlocks.set(block.id, block);
      }

      const newControls = new Map<string, DashboardControl>();
      for (const ctrl of dashboard.controls) {
        newControls.set(ctrl.id, ctrl);
      }

      dispatch({
        type: 'INIT_DASHBOARD',
        dashboard,
        messages: state.chatMessages,
        threadId: threadIdRef.current,
      });

      if (dashboard.blocks.length > 0) {
        const dataResult = await dashboardApi.fetchAllData(dashboardId);
        const blockData = new Map<string, Record<string, unknown>[]>();
        if (dataResult.data && Array.isArray(dataResult.data)) {
          for (const item of dataResult.data) {
            if (item.success && item.data) {
              blockData.set(item.block_id, item.data);
            }
          }
        }
        dispatch({ type: 'DATA_REFRESHED', blockData });
      }
    } catch {
      // Non-fatal: dashboard stays as-is
    }
  }, [state.chatMessages]);

  /**
   * Core SSE handler shared by sendMessage and sendFeedback.
   * Streams events from the agent runtime and dispatches reducer actions.
   */
  const runStreamingConversation = useCallback(
    async (
      dashboardId: string,
      content: string,
      metadata?: { type: string; block_id: string; block_title: string },
    ) => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      dispatch({ type: 'STREAMING_RESET' });
      dispatch({ type: 'LLM_RESPONDING', responding: true });

      let accumulatedText = '';

      const handleEvent = (event: V2StreamEvent) => {
        switch (event.type) {
          case 'thread_created': {
            const newThreadId = event.threadId as string;
            threadIdRef.current = newThreadId;
            dispatch({ type: 'SET_THREAD_ID', threadId: newThreadId });
            break;
          }

          case 'text_segment_start':
            dispatch({ type: 'STREAMING_TEXT_SEGMENT_START' });
            break;

          case 'text_delta':
            accumulatedText += event.delta as string;
            dispatch({ type: 'STREAMING_TEXT_DELTA', delta: event.delta as string });
            break;

          case 'tool_start':
            dispatch({
              type: 'STREAMING_TOOL_START',
              toolName: event.toolName as string,
              toolCallId: event.toolCallId as string,
            });
            break;

          case 'tool_result':
            dispatch({
              type: 'STREAMING_TOOL_RESULT',
              toolCallId: event.toolCallId as string,
              success: event.success as boolean,
            });
            break;

          case 'delegation_start':
            dispatch({
              type: 'STREAMING_TOOL_START',
              toolName: `Building: ${(event.targetAgentId as string).replace(/-/g, ' ')}`,
              toolCallId: event.toolCallId as string,
            });
            break;

          case 'delegation_complete':
            dispatch({
              type: 'STREAMING_TOOL_RESULT',
              toolCallId: event.toolCallId as string,
              success: event.success as boolean,
            });
            break;

          case 'error':
            dispatch({
              type: 'SET_ERROR',
              error: event.error as string,
            });
            break;

          // turn_start, thinking_delta, hook_start, hook_complete, complete: no-op in reducer
        }
      };

      try {
        await dashboardApi.sendMessageStream(
          dashboardId,
          content,
          handleEvent,
          metadata,
          controller.signal,
        );

        // Stream ended — construct assistant message from accumulated text
        if (accumulatedText) {
          const assistantMsg: ChatMessage = {
            id: `stream-${Date.now()}`,
            thread_id: threadIdRef.current || '',
            role: 'assistant',
            segments: [{ type: 'text', text: accumulatedText }],
            thinking_content: null,
            triggered_by_llm_interaction_id: null,
            attachments: null,
            created_at: new Date().toISOString(),
          };
          dispatch({ type: 'CHAT_MESSAGE_ADDED', messages: [assistantMsg] });
        }

        // Reload dashboard to pick up any structural changes from tools
        await reloadDashboardState();
      } catch (err: unknown) {
        if (err instanceof Error && err.name === 'AbortError') return;
        dispatch({
          type: 'SET_ERROR',
          error: err instanceof Error ? err.message : 'Failed to send message',
        });
      } finally {
        dispatch({ type: 'LLM_RESPONDING', responding: false });
        dispatch({ type: 'STREAMING_RESET' });
      }
    },
    [reloadDashboardState],
  );

  const abortStream = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    dispatch({ type: 'LLM_RESPONDING', responding: false });
    dispatch({ type: 'STREAMING_RESET' });
  }, []);

  const sendMessage = useCallback(
    async (content: string) => {
      const dashboardId = dashboardIdRef.current;
      if (!dashboardId) return;

      const tempUserMsg: ChatMessage = {
        id: `temp-${Date.now()}`,
        thread_id: threadIdRef.current || '',
        role: 'user',
        segments: [{ type: 'text', text: content }],
        thinking_content: null,
        triggered_by_llm_interaction_id: null,
        attachments: null,
        created_at: new Date().toISOString(),
      };
      dispatch({ type: 'CHAT_MESSAGE_ADDED', messages: [tempUserMsg] });

      await runStreamingConversation(dashboardId, content);
    },
    [runStreamingConversation],
  );

  const buildControlValues = useCallback(
    (controlVals: Record<string, unknown>): ControlValues | undefined => {
      if (Object.keys(controlVals).length === 0) return undefined;

      const cv: ControlValues = {};
      for (const [, value] of Object.entries(controlVals)) {
        if (
          value &&
          typeof value === 'object' &&
          'type' in (value as Record<string, unknown>)
        ) {
          const tr = value as Record<string, unknown>;
          const trType = tr.type as string;
          if (['last_n_days', 'last_n_weeks', 'specific_range'].includes(trType)) {
            cv.time_range = value as ControlValues['time_range'];
          }
        }
      }
      return Object.keys(cv).length > 0 ? cv : undefined;
    },
    []
  );

  const refreshData = useCallback(async () => {
    const dashboardId = dashboardIdRef.current;
    if (!dashboardId) return;

    try {
      const controlValues = buildControlValues(state.activeControlValues);
      const result = await dashboardApi.fetchAllData(dashboardId, controlValues);
      const blockData = new Map<string, Record<string, unknown>[]>();
      if (result.data && Array.isArray(result.data)) {
        for (const item of result.data) {
          if (item.success && item.data) {
            blockData.set(item.block_id, item.data);
          }
        }
      }
      dispatch({ type: 'DATA_REFRESHED', blockData });
    } catch {
      // Silent failure — data stays as-is
    }
  }, [state.activeControlValues, buildControlValues]);

  const updateBlockStatus = useCallback(
    async (blockId: string, status: BlockStatus) => {
      const dashboardId = dashboardIdRef.current;
      if (!dashboardId) return;

      const block = state.blocks.get(blockId);
      const previousStatus = block?.status;

      dispatch({ type: 'BLOCK_STATUS_CHANGED', blockId, status });

      const noticeText =
        status === 'accepted'
          ? `Kept: ${block?.title ?? 'Block'}`
          : status === 'rejected'
            ? `Discarded: ${block?.title ?? 'Block'}`
            : `Restored: ${block?.title ?? 'Block'}`;

      dispatch({
        type: 'ADD_SYSTEM_NOTICE',
        notice: {
          id: `notice-${Date.now()}-${blockId}`,
          text: noticeText,
          timestamp: new Date().toISOString(),
          icon: status === 'rejected' ? 'x' : 'check',
        },
      });

      try {
        await dashboardApi.updateBlockStatus(dashboardId, blockId, status);
      } catch {
        if (previousStatus) {
          dispatch({ type: 'BLOCK_STATUS_CHANGED', blockId, status: previousStatus });
        }
      }
    },
    [state.blocks]
  );

  const sendFeedback = useCallback(
    async (blockId: string, blockTitle: string, feedback: string) => {
      const dashboardId = dashboardIdRef.current;
      if (!dashboardId) return;

      const tempUserMsg: ChatMessage = {
        id: `temp-${Date.now()}`,
        thread_id: threadIdRef.current || '',
        role: 'user',
        segments: [{ type: 'text', text: feedback }],
        thinking_content: null,
        triggered_by_llm_interaction_id: null,
        attachments: [{ type: 'block_reference', data: { block_id: blockId, block_title: blockTitle } }],
        created_at: new Date().toISOString(),
      };
      dispatch({ type: 'CHAT_MESSAGE_ADDED', messages: [tempUserMsg] });

      const metadata = { type: 'chart_feedback', block_id: blockId, block_title: blockTitle };
      await runStreamingConversation(dashboardId, feedback, metadata);
    },
    [runStreamingConversation],
  );

  const changeControlValue = useCallback(
    async (parameterKey: string, value: unknown) => {
      const dashboardId = dashboardIdRef.current;
      if (!dashboardId) return;

      dispatch({ type: 'CONTROL_VALUE_CHANGED', parameterKey, value });
      dispatch({ type: 'SET_REFRESHING', refreshing: true });

      try {
        const updatedValues = { ...state.activeControlValues, [parameterKey]: value };
        const controlValues = buildControlValues(updatedValues);
        const result = await dashboardApi.fetchAllData(dashboardId, controlValues);
        const blockData = new Map<string, Record<string, unknown>[]>();
        if (result.data && Array.isArray(result.data)) {
          for (const item of result.data) {
            if (item.success && item.data) {
              blockData.set(item.block_id, item.data);
            }
          }
        }
        dispatch({ type: 'DATA_REFRESHED', blockData });
      } catch {
        toast.error('Failed to refresh data with new control values');
      } finally {
        dispatch({ type: 'SET_REFRESHING', refreshing: false });
      }
    },
    [state.activeControlValues, buildControlValues]
  );

  const togglePreviewMode = useCallback(() => {
    dispatch({ type: 'SET_PREVIEW_MODE', preview: !state.isPreviewMode });
  }, [state.isPreviewMode]);

  const updateBlockPositions = useCallback(
    (positions: { blockId: string; position: Position }[]) => {
      dispatch({ type: 'BLOCK_POSITIONS_UPDATED', positions });
    },
    []
  );

  const saveDashboard = useCallback(async () => {
    const dashboardId = dashboardIdRef.current;
    if (!dashboardId) return;

    try {
      const allBlocks = Array.from(state.blocks.values());
      const acceptedPositions = allBlocks
        .filter((b) => b.status === 'accepted')
        .map((b) => ({ block_id: b.id, position: b.position }));

      if (acceptedPositions.length > 0) {
        await dashboardApi.updateBlockPositions(dashboardId, acceptedPositions);
      }

      await dashboardApi.update(dashboardId, { status: 'saved' });
      dispatch({ type: 'SETTINGS_UPDATED', settings: { status: 'saved' } });
      toast.success('Dashboard saved!');
    } catch {
      toast.error('Failed to save dashboard');
    }
  }, [state.blocks]);

  const ctxValue: DashboardBuildContextValue = {
    state,
    renderHandles,
    sendMessage,
    refreshData,
    initDashboard,
    updateBlockStatus,
    sendFeedback,
    changeControlValue,
    togglePreviewMode,
    saveDashboard,
    updateBlockPositions,
    abortStream,
  };

  return (
    <DashboardBuildContext.Provider value={ctxValue}>
      {children}
    </DashboardBuildContext.Provider>
  );
};

// ============================================================================
// HOOK
// ============================================================================

export function useDashboardBuild(): DashboardBuildContextValue {
  const ctx = useContext(DashboardBuildContext);
  if (!ctx) {
    throw new Error(
      'useDashboardBuild must be used within a DashboardBuildProvider'
    );
  }
  return ctx;
}
